import React, { useState } from 'react';
import { Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import {
  themeColors,
  useDialog,
  useGlobalContext
} from '@applications-terrains/birdz-react-library';
import { useTranslation } from 'react-i18next';
import { useGetExocetTechnicians } from '../../../../../../hooks/datarefs';
import ConfirmDialog, { ConfirmDialogOptions } from '../../ConfirmDialog';
import { useInterventionCreation } from '../../context/interventionCreationContext';
import TransitionReportBanner from './TransitionReportBanner';
import { AssignmentTransitionState } from './useAssignmentTransition';
import { useAssignTechnician } from './useAssignTechnician';
import { useRemoveAssignment } from './useRemoveAssignment';
import { hasTechnician } from './assignmentRows';
import { useRequestsToAssign } from './useRequestsToAssign';

/** Valeur du select quand rien n'est choisi : `null` ne peut pas servir de value MUI. */
const NO_SELECTION = '';

/** Le hook de référentiel renvoie `any[]` : on nomme la forme qu'il produit. */
type TechnicianOption = { value: number; label: string };

type TechnicianPanelProps = {
  transitionState: AssignmentTransitionState;
};

export default function TechnicianPanel({ transitionState }: TechnicianPanelProps) {
  const { t } = useTranslation();
  const { state, dispatch } = useInterventionCreation();
  // La maquette montre un titre orange, mais c'est la couleur d'accentuation du thème
  // choisi par l'utilisateur qui prime : `themeColors` couvre les six thèmes.
  const { theme } = useGlobalContext();
  const { confirmDialog, closeDialog, dialogOptions } = useDialog();

  const { data: technicians, isFetching: isLoadingTechnicians } = useGetExocetTechnicians(
    state.clientId === null ? '' : String(state.clientId)
  );
  const assignState = useAssignTechnician();
  const { removeAssignment, isRemoving } = useRemoveAssignment();

  // Sans filtre : même clé de requête que le compteur du stepper, donc même cache et
  // aucun appel supplémentaire. La liste n'est pas paginée (`size=9999`), toute demande
  // cochée s'y trouve.
  const { rows } = useRequestsToAssign();

  const [technicianId, setTechnicianId] = useState<number | null>(null);

  /**
   * Le technicien choisi reste affiché après l'affectation : les demandes restent
   * cochées, l'utilisateur doit pouvoir lire à qui il vient de les confier.
   *
   * Il n'est reposé que lorsque la sélection de demandes change. MUI ne déclenche pas
   * `onChange` sur une valeur identique : sans cette remise à zéro, confier de nouvelles
   * demandes au même technicien resterait sans effet. L'ajustement se fait au rendu et
   * non dans un `useEffect`, pour éviter un rendu intermédiaire avec l'ancien choix.
   */
  const selectionKey = state.selectedRequestIds.join(',');
  const [lastSelectionKey, setLastSelectionKey] = useState(selectionKey);

  if (selectionKey !== lastSelectionKey) {
    setLastSelectionKey(selectionKey);
    setTechnicianId(null);
  }

  const technicianOptions: TechnicianOption[] = technicians ?? [];
  const selectedCount = state.selectedRequestIds.length;
  const hasSelection = selectedCount > 0;

  /**
   * Désaffecter n'a de sens que si au moins une demande cochée porte un technicien.
   *
   * Une sélection mixte reste permise : l'action porte alors sur celles qui en ont un.
   */
  const selectedIds = new Set(state.selectedRequestIds);
  const assignedSelectionCount = rows.filter(
    (row) => selectedIds.has(row.id) && hasTechnician(row)
  ).length;
  const hasAssignedSelection = assignedSelectionCount > 0;
  const isBusy = assignState.isSaving || isRemoving || transitionState.isRunning;

  /** Le choix d'un technicien affecte aussitôt, sans bouton de validation. */
  const selectTechnician = (value: string) => {
    if (value === NO_SELECTION) {
      setTechnicianId(null);
      return;
    }

    const code = Number(value);
    setTechnicianId(code);
    assignState.assignTechnician(code);
  };

  const askToUnassign = () => {
    const options: ConfirmDialogOptions = {
      title: t('interventionCreation.assignment.unassignConfirmTitle'),
      content: t('interventionCreation.assignment.unassignConfirmContent', {
        count: assignedSelectionCount
      }),
      onValidate: () => {
        // Fermer avant de lancer : deux modales superposées si la mutation échoue.
        closeDialog();
        removeAssignment(state.selectedRequestIds);
      },
      onCancel: () => closeDialog()
    };

    confirmDialog(options);
  };

  const resetSelection = () => dispatch({ type: 'setSelectedRequests', ids: [] });

  return (
    <>
      <ConfirmDialog options={dialogOptions} />

      <Paper sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 16 } }}>
        <Stack spacing={1.5}>
          <Typography variant="h6" sx={{ color: themeColors[theme], fontWeight: 700 }}>
            {t('interventionCreation.assignment.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('interventionCreation.assignment.subtitle')}
          </Typography>

          <Stack spacing={1} sx={{ pt: 1 }}>
            <Typography variant="body2" id="assignment-technician-label">
              {`${t('interventionCreation.assignment.technician')} *`}
            </Typography>
            <TextField
              select
              id="assignment-technician"
              fullWidth
              size="small"
              SelectProps={{ labelId: 'assignment-technician-label', displayEmpty: true }}
              value={technicianId ?? NO_SELECTION}
              disabled={!hasSelection || isLoadingTechnicians || isBusy}
              onChange={(event) => selectTechnician(event.target.value)}
            >
              <MenuItem value={NO_SELECTION}>
                {t('interventionCreation.assignment.technicianPlaceholder')}
              </MenuItem>
              {technicianOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {hasSelection && (
              <Typography variant="caption" color="text.secondary">
                {t('interventionCreation.assignment.technicianImmediate', {
                  total: selectedCount
                })}
              </Typography>
            )}
            {assignState.hasFailed && (
              <Typography variant="caption" color="error">
                {t('interventionCreation.assignment.technicianError')}
              </Typography>
            )}
            {/* Le back peut refuser une demande dont le contrat n'est pas dans le périmètre
                du technicien : le lot répond 200, le motif est dans le bilan. */}
            {assignState.report !== null && (
              <TransitionReportBanner
                report={assignState.report}
                successMessage={t('interventionCreation.assignment.report.assigned', {
                  count: assignState.report.succeededCount
                })}
                onDismiss={assignState.dismissReport}
              />
            )}
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 1 }}>
            <Button
              variant="outlined"
              disabled={!hasAssignedSelection || isBusy}
              onClick={askToUnassign}
            >
              {t('interventionCreation.assignment.unassign', { total: selectedCount })}
            </Button>
            <Button
              variant="contained"
              disabled={!hasSelection || isBusy}
              onClick={() => transitionState.runTransition('send')}
            >
              {t('interventionCreation.assignment.send', { total: selectedCount })}
            </Button>
          </Box>
          {hasSelection && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" disabled={isBusy} onClick={resetSelection}>
                {t('interventionCreation.assignment.clearSelection')}
              </Button>
            </Box>
          )}
        </Stack>
      </Paper>
    </>
  );
}
